/**
 * The arena session: the run the Game plate plays.
 *
 * The plate is a game engine. It steps the board, paints it, and reads one
 * decision per step from the arena session bundled with the page - the same run
 * for every visitor, so the plate costs nothing at serve time and every visitor
 * watches the same brain play. The session is the engine's own data: the steps
 * of one continuous run, each with the real Arc block, thought tick, state root
 * and fired count that decision was made under.
 *
 * This module is the boundary that reads it: a malformed session must fail
 * loudly rather than paint a board that never happened.
 */

import {
  asArray,
  asBoolean,
  asNumber,
  asString,
  DecodeError,
  field,
  optionalField,
  type JsonValue,
} from "../json";
import { DIRECTIONS, type Direction, type Features } from "./arena";
import type { Decision, DecisionSource, MoveRecord, ThoughtState } from "./decide";
import type { GuardOutcome } from "./guard";

export type SessionInfo = {
  readonly version: number;
  readonly madeAt: string;
  readonly engine: string;
  readonly policy: string;
  readonly chainId: number;
  readonly brain: string;
  readonly board: number;
  readonly seed: number;
  /** Steps per second the run was played at: the reference demo's pace. */
  readonly pace: number;
  readonly foods: number;
  readonly episodes: number;
};

export type ArenaSession = {
  readonly info: SessionInfo;
  readonly steps: readonly MoveRecord[];
};

export type Cursor = {
  readonly at: number;
  readonly episode: number;
};

function isDirection(value: string): value is Direction {
  return DIRECTIONS.some((known) => known === value);
}

function readDirection(value: JsonValue, label: string): Direction {
  const name = asString(value, label);

  if (!isDirection(name)) throw new DecodeError(`${label}: unknown direction "${name}"`);

  return name;
}

function readDirections(value: JsonValue, label: string): Direction[] {
  return asArray(value, label).map((each, at) => readDirection(each, `${label}[${at}]`));
}

function readBlock(value: JsonValue): MoveRecord["block"] {
  return {
    number: asNumber(field(value, "number", "block"), "block.number"),
    hash: asString(field(value, "hash", "block"), "block.hash"),
    timestamp: asNumber(field(value, "timestamp", "block"), "block.timestamp"),
    txs: asNumber(field(value, "txs", "block"), "block.txs"),
  };
}

function readThought(value: JsonValue): ThoughtState {
  const root = optionalField(value, "stateRoot", "thought");

  return {
    tick: asNumber(field(value, "tick", "thought"), "thought.tick"),
    stateRoot: root === null ? null : asString(root, "thought.stateRoot"),
    fired: asNumber(field(value, "fired", "thought"), "thought.fired"),
    spiked: asNumber(field(value, "spiked", "thought"), "thought.spiked"),
    digest: asString(field(value, "digest", "thought"), "thought.digest"),
    bias: readDirection(field(value, "bias", "thought"), "thought.bias"),
    reused: asBoolean(field(value, "reused", "thought"), "thought.reused"),
  };
}

function readFeatures(value: JsonValue): Features {
  const food = field(value, "food", "features");

  return {
    food: {
      dx: asNumber(field(food, "dx", "food"), "food.dx"),
      dy: asNumber(field(food, "dy", "food"), "food.dy"),
    },
    foodDirections: readDirections(field(value, "foodDirections", "features"), "foodDirections"),
    danger: readDirections(field(value, "danger", "features"), "danger"),
    safe: readDirections(field(value, "safe", "features"), "safe"),
    freeSpace: asNumber(field(value, "freeSpace", "features"), "freeSpace"),
    freeSpaceRatio: asNumber(field(value, "freeSpaceRatio", "features"), "freeSpaceRatio"),
    length: asNumber(field(value, "length", "features"), "length"),
    cells: asNumber(field(value, "cells", "features"), "cells"),
  };
}

function readDecision(value: JsonValue): Decision {
  const raw = field(value, "probabilities", "decision");
  const probabilities: Record<Direction, number> = { up: 0, down: 0, left: 0, right: 0 };

  for (const direction of DIRECTIONS) {
    probabilities[direction] = asNumber(
      field(raw, direction, "decision.probabilities"),
      `decision.probabilities.${direction}`,
    );
  }

  return {
    direction: readDirection(field(value, "direction", "decision"), "decision.direction"),
    probabilities,
    confidence: asNumber(field(value, "confidence", "decision"), "decision.confidence"),
    confidenceScale: asNumber(
      field(value, "confidenceScale", "decision"),
      "decision.confidenceScale",
    ),
    modelConfidence: asNumber(
      field(value, "modelConfidence", "decision"),
      "decision.modelConfidence",
    ),
    risk: asNumber(field(value, "risk", "decision"), "decision.risk"),
  };
}

function readGuard(value: JsonValue): GuardOutcome {
  const kind = asString(field(value, "kind", "guard"), "guard.kind");

  if (kind === "trapped") return { kind: "trapped" };

  if (kind === "accepted")
    return {
      kind: "accepted",
      direction: readDirection(field(value, "direction", "guard"), "guard.direction"),
    };

  if (kind !== "overridden") throw new DecodeError(`guard.kind: unknown kind "${kind}"`);

  const reason = asString(field(value, "reason", "guard"), "guard.reason");

  if (reason !== "fatal-move" && reason !== "cycle-guard")
    throw new DecodeError(`guard.reason: unknown reason "${reason}"`);

  return {
    kind: "overridden",
    direction: readDirection(field(value, "direction", "guard"), "guard.direction"),
    reason,
  };
}

function readSource(value: JsonValue): DecisionSource {
  const kind = asString(field(value, "kind", "source"), "source.kind");

  if (kind === "policy")
    return {
      kind: "policy",
      engine: asString(field(value, "engine", "source"), "source.engine"),
    };

  if (kind === "jev")
    return {
      kind: "jev",
      model: asString(field(value, "model", "source"), "source.model"),
      latencyMs: asNumber(field(value, "latencyMs", "source"), "source.latencyMs"),
    };

  if (kind !== "jev-failed") throw new DecodeError(`source.kind: unknown kind "${kind}"`);

  return {
    kind: "jev-failed",
    reason: asString(field(value, "reason", "source"), "source.reason"),
  };
}

function readStep(value: JsonValue): MoveRecord {
  const room = field(value, "room", "step");

  return {
    move: asNumber(field(value, "move", "step"), "step.move"),
    block: readBlock(field(value, "block", "step")),
    thought: readThought(field(value, "thought", "step")),
    features: readFeatures(field(value, "features", "step")),
    decision: readDecision(field(value, "decision", "step")),
    applied: readDirection(field(value, "applied", "step"), "step.applied"),
    guard: readGuard(field(value, "guard", "step")),
    room: {
      proposed: asNumber(field(room, "proposed", "room"), "room.proposed"),
      best: asNumber(field(room, "best", "room"), "room.best"),
    },
    slot: asNumber(field(value, "slot", "step"), "step.slot"),
    planSize: asNumber(field(value, "planSize", "step"), "step.planSize"),
    source: readSource(field(value, "source", "step")),
  };
}

/** Reads the bundled arena session, and refuses one that would paint a lie. */
export function readSession(payload: JsonValue): ArenaSession {
  const info = field(payload, "info", "session");
  const raw = asArray(field(payload, "steps", "session"), "session.steps");

  if (raw.length === 0) throw new DecodeError("session.steps: a session needs at least one step");

  const steps = raw.map((each) => readStep(each));

  steps.forEach((step, at) => {
    if (step.move !== at + 1)
      throw new DecodeError(`session.steps[${at}].move: expected ${at + 1}, got ${step.move}`);
  });

  return {
    info: {
      version: asNumber(field(info, "version", "info"), "info.version"),
      madeAt: asString(field(info, "madeAt", "info"), "info.madeAt"),
      engine: asString(field(info, "engine", "info"), "info.engine"),
      policy: asString(field(info, "policy", "info"), "info.policy"),
      chainId: asNumber(field(info, "chainId", "info"), "info.chainId"),
      brain: asString(field(info, "brain", "info"), "info.brain"),
      board: asNumber(field(info, "board", "info"), "info.board"),
      seed: asNumber(field(info, "seed", "info"), "info.seed"),
      pace: asNumber(field(info, "pace", "info"), "info.pace"),
      foods: asNumber(field(info, "foods", "info"), "info.foods"),
      episodes: asNumber(field(info, "episodes", "info"), "info.episodes"),
    },
    steps,
  };
}

/** The step a cursor points at. */
export function stepAt(session: ArenaSession, cursor: Cursor): MoveRecord {
  const step = session.steps[cursor.at];

  if (step === undefined) throw new Error(`the session has no step ${cursor.at}`);

  return step;
}

/**
 * The next cursor: one step on, or back to the first step with the episode
 * counted when the run ends. One run follows the next without a seam, which is
 * how the plate plays continuously.
 */
export function advance(session: ArenaSession, cursor: Cursor): Cursor {
  const at = cursor.at + 1;

  return at >= session.steps.length
    ? { at: 0, episode: cursor.episode + 1 }
    : { at, episode: cursor.episode };
}

/** The apple's cell relative to a head, as the plate's arrows read it. */
export function foodVector(food: { readonly dx: number; readonly dy: number }): string {
  const vertical = food.dy === 0 ? "" : `${food.dy < 0 ? "↑" : "↓"}${Math.abs(food.dy)}`;
  const across = food.dx === 0 ? "" : `${food.dx < 0 ? "←" : "→"}${Math.abs(food.dx)}`;

  return `${vertical} ${across}`.trim();
}
